import * as THREE from 'three'
import { SCENE_CONSTANTS } from './constants.js'

export class KeyboardAssembler {
  constructor (keycapRenderer) {
    this.keycapRenderer = keycapRenderer
  }

  createKeyboardGroup (models, switchPositions, rotationData, config) {
    // All keyboards are split, so always use split keyboard logic
    return this.createSplitKeyboard(models, switchPositions, rotationData, config)
  }

  createKeyboardHalf (models, switchPositions, rotationData, config) {
    const componentsGroup = new THREE.Group()
    componentsGroup.position.y = SCENE_CONSTANTS.POSITIONS.KEYBOARD_GROUP_Y

    const caseModel = this.createCaseModel(models.caseGeometry)
    const boardModel = this.createBoardModel(models.boardGeometry, componentsGroup)
    const switchGroup = this.createSwitchGroup(models.switchScene, switchPositions, rotationData)
    const keycapGroup = this.createKeycapGroup(models.keycapGeometry, switchPositions, rotationData, config)

    componentsGroup.add(switchGroup)
    componentsGroup.add(keycapGroup)

    this.applyKeyboardRotation(componentsGroup, rotationData)

    // Create a half group that contains both components and case
    const halfGroup = new THREE.Group()
    halfGroup.add(componentsGroup)
    halfGroup.add(caseModel)

    return {
      halfGroup,
      componentsGroup,
      caseModel,
      boardModel
    }
  }

  createCaseModel (geometry) {
    const material = new THREE.MeshStandardMaterial({
      color: SCENE_CONSTANTS.COLORS.DEFAULT_CASE,
      roughness: SCENE_CONSTANTS.MATERIALS.CASE_ROUGHNESS,
      metalness: SCENE_CONSTANTS.MATERIALS.CASE_METALNESS
    })

    const caseModel = new THREE.Mesh(geometry, material)
    caseModel.castShadow = true
    caseModel.receiveShadow = true
    caseModel.rotation.x = -Math.PI / 2

    const box = new THREE.Box3().setFromObject(caseModel)
    const center = box.getCenter(new THREE.Vector3())
    caseModel.position.sub(center)

    return caseModel
  }

  createBoardModel (geometry, componentsGroup) {
    const material = new THREE.MeshStandardMaterial({
      color: SCENE_CONSTANTS.COLORS.DEFAULT_BOARD,
      roughness: SCENE_CONSTANTS.MATERIALS.BOARD_ROUGHNESS,
      metalness: SCENE_CONSTANTS.MATERIALS.BOARD_METALNESS
    })

    const boardModel = new THREE.Mesh(geometry, material)
    boardModel.castShadow = true
    boardModel.receiveShadow = true
    boardModel.rotation.x = -Math.PI / 2

    const box = new THREE.Box3().setFromObject(boardModel)
    const center = box.getCenter(new THREE.Vector3())
    boardModel.position.sub(center)
    boardModel.position.y = SCENE_CONSTANTS.POSITIONS.BOARD_Y

    componentsGroup.add(boardModel)
    return boardModel
  }

  createSwitchGroup (switchScene, switchPositions, rotationData) {
    const switchGroup = new THREE.Group()
    switchGroup.position.y = SCENE_CONSTANTS.POSITIONS.SWITCH_GROUP_Y

    const centerOffset = this.calculatePositionOffset(switchPositions, rotationData)

    switchPositions.forEach((position) => {
      const switchInstance = switchScene.clone()

      switchInstance.userData.isSwitch = true
      switchInstance.userData.position = position

      this.applySwitchMaterials(switchInstance)

      switchInstance.position.set(
        position.x - centerOffset.x,
        SCENE_CONSTANTS.POSITIONS.SWITCH_Y,
        -(position.y - centerOffset.y)
      )

      if (position.r !== 0) {
        switchInstance.rotation.y = position.r * (Math.PI / 180)
      }

      switchGroup.add(switchInstance)
    })

    return switchGroup
  }

  createKeycapGroup (keycapGeometry, switchPositions, rotationData, config) {
    this.keycapRenderer.generatePlanarUVs(keycapGeometry)

    const keycapGroup = new THREE.Group()
    keycapGroup.position.y = SCENE_CONSTANTS.POSITIONS.KEYCAP_GROUP_Y

    const centerOffset = this.calculatePositionOffset(switchPositions, rotationData)

    switchPositions.forEach((position) => {
      const keycapInstance = this.createKeycapInstance(
        keycapGeometry,
        position,
        centerOffset,
        config
      )
      keycapGroup.add(keycapInstance)
    })

    return keycapGroup
  }

  createKeycapInstance (geometry, position, centerOffset, config) {
    // Use keymapName if available (for right half), otherwise use position name
    const keymapKey = position.keymapName || position.name
    const legend = this.getKeycapLegend(keymapKey, config.transformedKeyMap)
    
    // Debug logging
    if (position.keymapName) {
      console.log(`Right half key: ${position.name} -> keymap: ${position.keymapName} -> legend: "${legend}"`)
    }
    const material = this.keycapRenderer.createKeycapMaterial(legend, {
      showLegends: true,
      isRightHalf: !!position.keymapName
    })

    const keycapInstance = new THREE.Mesh(geometry, material)
    keycapInstance.userData.isKeycap = true
    keycapInstance.userData.position = position
    keycapInstance.userData.originalPosition = position.name

    keycapInstance.castShadow = true
    keycapInstance.receiveShadow = true

    const box = new THREE.Box3().setFromObject(keycapInstance)
    const center = box.getCenter(new THREE.Vector3())
    keycapInstance.position.sub(center)

    keycapInstance.rotation.x = -Math.PI / 2

    keycapInstance.position.set(
      position.x - centerOffset.x,
      SCENE_CONSTANTS.POSITIONS.KEYCAP_Y,
      -(position.y - centerOffset.y)
    )

    if (position.r !== 0) {
      keycapInstance.rotation.z = position.r * (Math.PI / 180)
    }

    return keycapInstance
  }

  applySwitchMaterials (switchInstance) {
    const materials = [
      new THREE.MeshStandardMaterial({
        color: SCENE_CONSTANTS.COLORS.SWITCH_HOUSING,
        transparent: true,
        opacity: SCENE_CONSTANTS.MATERIALS.SWITCH_HOUSING_OPACITY,
        roughness: SCENE_CONSTANTS.MATERIALS.SWITCH_HOUSING_ROUGHNESS,
        metalness: SCENE_CONSTANTS.MATERIALS.KEYCAP_METALNESS
      }),
      new THREE.MeshStandardMaterial({
        color: SCENE_CONSTANTS.COLORS.SWITCH_BASE,
        roughness: SCENE_CONSTANTS.MATERIALS.SWITCH_BASE_ROUGHNESS,
        metalness: SCENE_CONSTANTS.MATERIALS.SWITCH_BASE_METALNESS
      }),
      new THREE.MeshStandardMaterial({
        color: SCENE_CONSTANTS.COLORS.SWITCH_STEM,
        roughness: SCENE_CONSTANTS.MATERIALS.SWITCH_STEM_ROUGHNESS,
        metalness: SCENE_CONSTANTS.MATERIALS.SWITCH_STEM_METALNESS
      })
    ]

    let partIndex = 0
    switchInstance.traverse((child) => {
      if (child.isMesh) {
        child.material = materials[partIndex % materials.length]
        child.castShadow = true
        child.receiveShadow = true
        partIndex++
      }
    })
  }

  calculatePositionOffset (positions, rotationData = null) {
    if (positions.length === 0) {
      throw new Error('No switch positions provided')
    }

    if (rotationData && rotationData.pcbCenter) {
      return {
        x: rotationData.pcbCenter.x,
        y: rotationData.pcbCenter.y
      }
    }

    const xCoords = positions.map(p => p.x)
    const yCoords = positions.map(p => p.y)

    const minX = Math.min(...xCoords)
    const maxX = Math.max(...xCoords)
    const minY = Math.min(...yCoords)
    const maxY = Math.max(...yCoords)

    return {
      x: (minX + maxX) / 2 + 1.7,
      y: (minY + maxY) / 2 - 1.7
    }
  }

  applyKeyboardRotation (componentsGroup, rotationData) {
    if (rotationData && rotationData.boardRotation) {
      const rotX = rotationData.boardRotation.x * (Math.PI / 180)
      const rotY = rotationData.boardRotation.y * (Math.PI / 180)

      componentsGroup.rotation.x = rotX
      componentsGroup.rotation.z = -rotY
    }
  }

  getKeycapLegend (positionName, transformedKeyMap) {
    if (!transformedKeyMap || typeof transformedKeyMap !== 'object') {
      throw new Error('Invalid transformed keymap provided')
    }

    return transformedKeyMap[positionName] || ''
  }

  createRightHalfPositions (leftPositions, config) {
    const rightPositions = []
    
    leftPositions.forEach(position => {
      // Create a mirrored position for the right half
      const rightPosition = { ...position }
      
      // For matrix positions, adjust the column index for keymap lookup
      const colMatch = position.name.match(/matrix_col(\d+)_row(\d+)/)
      if (colMatch) {
        const leftColIndex = parseInt(colMatch[1], 10)
        const rowIndex = parseInt(colMatch[2], 10)
        // Mirror the column index: rightmost left becomes leftmost right
        const mirroredColIndex = (config.columns - 1) - leftColIndex
        const rightColIndex = config.columns + mirroredColIndex
        
        // Keep original name for geometry, but add keymapName for legend lookup
        rightPosition.keymapName = `matrix_col${rightColIndex}_row${rowIndex}`
        console.log(`Creating right position: ${position.name} (leftCol=${leftColIndex}) -> keymapName: ${rightPosition.keymapName} (rightCol=${rightColIndex})`)
      } else if (position.name.startsWith('thumbs_')) {
        // For thumb keys, map to the right half thumb keys in the keymap
        const thumbMatch = position.name.match(/thumbs_thumb(\d+)_cluster/)
        if (thumbMatch) {
          const leftThumbIndex = parseInt(thumbMatch[1], 10)
          // Count total thumb keys by checking how many thumb keys exist for left side
          const leftThumbKeys = leftPositions.filter(p => p.name.startsWith('thumbs_')).length
          // Mirror the thumb index: rightmost left thumb becomes leftmost right thumb
          const mirroredThumbIndex = leftThumbKeys - leftThumbIndex + 1
          const rightThumbIndex = leftThumbKeys + mirroredThumbIndex
          rightPosition.keymapName = `thumbs_thumb${rightThumbIndex}_cluster`
          console.log(`Creating right thumb: ${position.name} (leftThumb=${leftThumbIndex}) -> keymapName: ${rightPosition.keymapName} (rightThumb=${rightThumbIndex})`)
        } else {
          rightPosition.keymapName = position.name
        }
      }
      
      rightPositions.push(rightPosition)
    })
    
    return rightPositions
  }

  createSplitKeyboard (models, switchPositions, rotationData, config) {
    // Debug: log the keymap to see what's available
    console.log('Available keymap keys:', Object.keys(config.transformedKeyMap))
    console.log('Config columns:', config.columns)
    console.log('Left positions:', switchPositions.map(p => p.name).sort())
    
    // Use all positions for left half, generate mirrored positions for right half
    const leftPositions = switchPositions
    const rightPositions = this.createRightHalfPositions(leftPositions, config)
    
    // Create left half with original positions
    const leftHalf = this.createKeyboardHalf(models, leftPositions, rotationData, config)
    
    // Create right half with generated positions (not a clone)
    const rightHalf = this.createKeyboardHalf(models, rightPositions, rotationData, config)
    
    // Mirror the right half
    rightHalf.halfGroup.scale.x = -1
    
    // Position the halves based on their edges, not centers
    const spacing = SCENE_CONSTANTS.POSITIONS.SPLIT_KEYBOARD_SPACING
    
    // Calculate bounding box for each half to get their widths
    const leftBoundingBox = new THREE.Box3().setFromObject(leftHalf.halfGroup)
    const rightBoundingBox = new THREE.Box3().setFromObject(rightHalf.halfGroup)
    
    const leftWidth = leftBoundingBox.max.x - leftBoundingBox.min.x
    const rightWidth = rightBoundingBox.max.x - rightBoundingBox.min.x
    
    // Position so that the right edge of left half is at -spacing/2
    // and the left edge of right half is at +spacing/2
    leftHalf.halfGroup.position.x = -spacing / 2 - leftWidth / 2
    rightHalf.halfGroup.position.x = spacing / 2 + rightWidth / 2
    
    // Create container group for both halves
    const splitKeyboardGroup = new THREE.Group()
    splitKeyboardGroup.add(leftHalf.halfGroup)
    splitKeyboardGroup.add(rightHalf.halfGroup)
    
    return {
      splitKeyboardGroup,
      leftHalf,
      rightHalf
    }
  }
}

